// Feature flags by ENVIRONMENT (ASK-145 §2 — ring deployment: trunk-based + flags, promotion never
// editing). One commit builds dev and prod; the plane's `.env.<mode>` decides what is switched on.
// Anything not on the approved-and-deployed list (ASK-145 §4) is OFF on production by flag, so a
// promoted release cannot expose a surface whose E2E card has not completed.
//
//   VITE_FEATURE_CHECKOUT          feed    → each shop's feed `commerce.checkout` decides (dev)
//                                  catalog → checkout is closed on EVERY shop, whatever the feed says
//   VITE_FEATURE_INVITES           on/off  → audience invites (E2E207/208). No runtime surface exists
//                                            yet; this is the switch it reads when it lands.
//   VITE_FEATURE_DEV_MEDUSA        on/off  → the Kapow Medusa DEV store as the default cart on a dev
//                                            host (the M6 money loop, `cart-service.js`). It used to be
//                                            chosen by HOSTNAME alone — and `*.web.app` counted as a dev
//                                            host, so production's own default URL would have carted
//                                            against the dev Medusa store. A plane decides, not a host.
//   VITE_FEATURE_GOOGLE_SIGNIN     on/off  → "Continue with Google" on the sign-in gate. A plane whose
//                                            user pool has no Google provider (production today:
//                                            email + password only) must not draw a button that fails.
//   VITE_PLANE_WORD                alpha/beta → the ONE word customer/merchant copy uses for the
//                                            plane's stage ("Invited guests only during the beta.",
//                                            "Checkout opens at launch · beta"). RULED by Shaun
//                                            2026-10-16 (ASK-145 §11.8): beta on production, alpha on
//                                            dev. Copy, not access — so its closed value is the dev word.
//
// (VITE_FEATURE_BLENDER_FIXTURES — "contributed library fixtures offered in the palette" — was DELETED 2026-10-25,
// ASK-164 K-a / D-079 (iii): after the migration every kind is a library row, so a source flag would empty the
// production palette; the offer is the row's own `offeredTo` + D-077, read in studio/_content-browser.js.)
//
// 📌 ABSENCE IS NEVER A DEFAULT (RULING 32) — and here it resolves to OFF. `vite.config.ts` refuses
// to BUILD with a flag unset or unrecognised, so a real bundle always carries a declared value; under
// a non-Vite import (a pure test, a CLI) every flag reads its fail-closed value: checkout `catalog`,
// everything else off. A missing flag can therefore only ever hide a surface, never expose one.
//
// IMPORTANT: the exact `import.meta.env.VITE_*` pattern (no optional chaining) is what Vite
// static-replaces; anything else leaves `import.meta.env` unresolved in the browser.

export const FLAG_VALUES = Object.freeze({
	VITE_FEATURE_CHECKOUT: Object.freeze(["feed", "catalog"]),
	VITE_FEATURE_INVITES: Object.freeze(["on", "off"]),
	VITE_FEATURE_DEV_MEDUSA: Object.freeze(["on", "off"]),
	VITE_FEATURE_GOOGLE_SIGNIN: Object.freeze(["on", "off"]),
	VITE_PLANE_WORD: Object.freeze(["alpha", "beta"]),
});

/** The fail-closed value of each flag — what a missing or unreadable flag means. */
export const FLAG_CLOSED = Object.freeze({
	VITE_FEATURE_CHECKOUT: "catalog",
	VITE_FEATURE_INVITES: "off",
	VITE_FEATURE_DEV_MEDUSA: "off",
	VITE_FEATURE_GOOGLE_SIGNIN: "off",
	VITE_PLANE_WORD: "alpha",
});

/** Pure: resolve a raw env map into the feature set. Unknown/absent → the fail-closed value. */
export function parseFeatures(env = {}) {
	const pick = (key) => {
		const raw = typeof env[key] === "string" ? env[key].trim().toLowerCase() : "";
		return FLAG_VALUES[key].includes(raw) ? raw : FLAG_CLOSED[key];
	};
	return Object.freeze({
		checkout: pick("VITE_FEATURE_CHECKOUT"),
		invites: pick("VITE_FEATURE_INVITES") === "on",
		devMedusa: pick("VITE_FEATURE_DEV_MEDUSA") === "on",
		googleSignIn: pick("VITE_FEATURE_GOOGLE_SIGNIN") === "on",
		planeWord: pick("VITE_PLANE_WORD"),
	});
}

/** Pure: the build-time check `vite.config.ts` runs — every flag declared with a recognised value. */
export function featureEnvProblems(env = {}) {
	const problems = [];
	for (const [key, allowed] of Object.entries(FLAG_VALUES)) {
		const raw = typeof env[key] === "string" ? env[key].trim().toLowerCase() : "";
		if (!raw) problems.push(`${key} is unset (allowed: ${allowed.join(" | ")})`);
		else if (!allowed.includes(raw)) problems.push(`${key}=${env[key]} is not one of ${allowed.join(" | ")}`);
	}
	return problems;
}

function readBuildEnv() {
	const env = {};
	try { env.VITE_FEATURE_CHECKOUT = import.meta.env.VITE_FEATURE_CHECKOUT; } catch { /* not under Vite */ }
	try { env.VITE_FEATURE_INVITES = import.meta.env.VITE_FEATURE_INVITES; } catch { /* not under Vite */ }
	try { env.VITE_FEATURE_DEV_MEDUSA = import.meta.env.VITE_FEATURE_DEV_MEDUSA; } catch { /* not under Vite */ }
	try { env.VITE_FEATURE_GOOGLE_SIGNIN = import.meta.env.VITE_FEATURE_GOOGLE_SIGNIN; } catch { /* not under Vite */ }
	try { env.VITE_PLANE_WORD = import.meta.env.VITE_PLANE_WORD; } catch { /* not under Vite */ }
	return env;
}

/** The plane's features, resolved once at module load from the bundle's baked env. */
export const FEATURES = parseFeatures(readBuildEnv());

/** Checkout is closed on every shop by the plane, regardless of any shop's feed. */
export const checkoutForcedClosed = (features = FEATURES) => features.checkout === "catalog";

/** Audience invites may be offered on this plane. */
export const invitesEnabled = (features = FEATURES) => features.invites === true;

/** The Kapow Medusa dev store may be the default cart on a dev host of this plane. */
export const devMedusaEnabled = (features = FEATURES) => features.devMedusa === true;

/** The sign-in gate may offer "Continue with Google" on this plane. */
export const googleSignInEnabled = (features = FEATURES) => features.googleSignIn === true;

/** The plane's stage word for customer/merchant copy — "beta" on production, "alpha" on dev (§11.8). */
export const planeWord = (features = FEATURES) => features.planeWord;
